import React, { useState } from 'react';
import { CheckCircle2, Download, Trash2, ExternalLink, Facebook, Linkedin, Edit2, MessageSquare, ChevronDown, ChevronUp, AlertCircle, FileText, Search, User, Send, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WonkaPeopleTab } from './WonkaPeopleTab';
import { WonkaFinancialsTab } from './WonkaFinancialsTab';
import { WonkaDetailsTab } from './WonkaDetailsTab';
import { WonkaNotificationsTab } from './WonkaNotificationsTab';
import { WonkaOverviewTab } from './WonkaOverviewTab';
import { WonkaApplicationView } from './WonkaApplicationView';
import { WonkaResearchAgentsTab } from './WonkaResearchAgentsTab';
import { WonkaAuditLogTab } from './WonkaAuditLogTab';
import { NotesModal } from './NotesModal';
import { ScoutAIButton } from './ScoutAIButton';
const wonkaNotes = [{
  id: '1',
  type: 'user' as const,
  author: 'Sarah Chen',
  timestamp: 'February 20, 2026',
  title: 'Excellent Payment History',
  content: 'Wonka has consistently paid within terms for the past 12 months. Strong financials and growing market share. Recommend increasing credit limit to $150K if requested.'
}, {
  id: '2',
  type: 'system' as const,
  timestamp: 'January 15, 2026 · 3:45 PM',
  title: 'Credit Approved',
  content: 'Credit limit set to $100,000 with Net 30 terms. Score at approval: 86 (Excellent Standing).'
}, {
  id: '3',
  type: 'system' as const,
  timestamp: 'January 12, 2026 · 11:20 AM',
  title: 'Application Submitted',
  content: 'Submitted by Charlie Bucket on behalf of Wonka Industries.'
}, {
  id: '4',
  type: 'system' as const,
  timestamp: 'January 10, 2026 · 9:30 AM',
  title: 'Application Started',
  content: 'Credit application initiated for Wonka Industries.'
}];
export function WonkaCreditApplication() {
  const [activeTab, setActiveTab] = useState('OVERVIEW');
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<null | 'approve' | 'approveWithChanges' | 'reject'>(null);
  const [decisionMade, setDecisionMade] = useState<null | 'approved' | 'approvedWithChanges' | 'rejected'>(null);
  const [expandedRef, setExpandedRef] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const handleDecision = (decision: 'approved' | 'approvedWithChanges' | 'rejected') => {
    setDecisionMade(decision);
    setActiveModal(null);
  };
  return <div className="min-h-full bg-gray-50 p-6" data-id="element-3417" data-mp="3nn881x4q79m1">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-3418" data-mp="38stxmkz898iz">
        {/* Header Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-3419" data-mp="3dqvf51h08zsc">
          <div className="flex items-center justify-between gap-4" data-id="element-3420" data-mp="3dsye4b04y397">
            {/* Left: Company Info + Notes */}
            <div className="shrink-0" data-id="element-3421" data-mp="1bzib0emv2vxc">
              <h1 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-3422" data-mp="1byh2ohx8cnjb">
                WONKA INDUSTRIES
              </h1>
              <div className="flex items-center gap-3 text-indigo-600 mb-2" data-id="element-3423" data-mp="2wfq79w2i38yj">
                <ExternalLink className="w-3.5 h-3.5 cursor-pointer" data-id="element-3424" data-mp="2rk16xqinpo16" />
                <Facebook className="w-3.5 h-3.5 cursor-pointer" data-id="element-3425" data-mp="367vy32h0nkc5" />
                <Linkedin className="w-3.5 h-3.5 cursor-pointer" data-id="element-3426" data-mp="369fc0mf79jar" />
                <Edit2 className="w-3.5 h-3.5 cursor-pointer" data-id="element-3427" data-mp="31fb9cfcyvxfh" />
              </div>
              <div className="space-y-0.5 text-xs text-gray-600 mb-2" data-id="element-3428" data-mp="3f6zhy8o0c3rp">
                <div className="flex items-center gap-1.5" data-id="element-3429" data-mp="3b78kxn7w0req">
                  <span className="font-semibold text-gray-900" data-id="element-3430" data-mp="3b8rzn9dwauee">
                    Credit Manager:
                  </span>
                  <span data-id="element-3431" data-mp="3k7n70osoeaja">Sarah Chen</span>
                  <Edit2 className="w-3 h-3 text-indigo-600 cursor-pointer" data-id="element-3432" data-mp="3k8oszob8chfs" />
                </div>
                <div className="flex items-center gap-1.5" data-id="element-3433" data-mp="1ut8o4l4c9hvp">
                  <span className="font-semibold text-gray-900" data-id="element-3434" data-mp="1ptm9c78v3tks">Added:</span>
                  <span data-id="element-3435" data-mp="1pvpa9mbjz74a">1/10/2026</span>
                </div>
              </div>
              <button onClick={() => setShowNotesModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded flex items-center gap-1.5 text-xs font-medium relative" data-id="element-3436" data-mp="1ra9w10p2rw0t">
                <MessageSquare className="w-3 h-3" data-id="element-3437" data-mp="268bz79s72fhj" />
                NOTES
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-id="element-3438" data-mp="265g03i4m1kri">
                  {wonkaNotes.length}
                </span>
              </button>
            </div>

            {/* Right: Risk Gauge */}
            <div className="flex flex-col items-center shrink-0 mr-4" data-id="element-3439" data-mp="1igd7xgcco9ir">
              <div className="flex items-end gap-2 mb-1" data-id="element-3440" data-mp="1nahgg419kply">
                <span className="text-xs text-gray-400 font-medium" data-id="element-3441" data-mp="pu2bmhvs0urr">0</span>
                <div className="relative w-64" data-id="element-3442" data-mp="usnaacvdr6e3">
                  {/* Score label */}
                  <div className="absolute -top-6 left-[88%] -translate-x-1/2 bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded" data-id="element-3443" data-mp="uqajda7n9low">
                    88
                  </div>
                  {/* Bar */}
                  <div className="relative w-full h-6 flex rounded-full overflow-hidden" data-id="element-3444" data-mp="2z1yot0ohpdv0">
                    <div className="w-1/3 bg-red-300" data-id="element-3445" data-mp="2u344fuavjh8s"></div>
                    <div className="w-1/3 bg-yellow-300" data-id="element-3446" data-mp="2u2m5ur0qoqxp"></div>
                    <div className="w-1/3 bg-green-300" data-id="element-3447" data-mp="2p4as457bpwa7"></div>
                  </div>
                  {/* Indicator line */}
                  <div className="absolute top-0 bottom-0 left-[88%] w-0.5 flex flex-col items-center" data-id="element-3448" data-mp="2kbrfbaggn56g">
                    <div className="w-[3px] h-full bg-gray-700 rounded-full" style={{
                    borderLeft: '2px dashed #374151'
                  }} data-id="element-3449" data-mp="2gc8sg952wk0z"></div>
                  </div>
                </div>
                <span className="text-xs text-gray-400 font-medium" data-id="element-3450" data-mp="21frjtpo9xji4">100</span>
              </div>
              <div className="w-64 flex justify-between px-0 mt-1" data-id="element-3451" data-mp="1rpt52ogruq3h">
                <span className="text-xs text-red-500 font-medium pl-7" data-id="element-3452" data-mp="1wpnpqa9yejg3">
                  Low
                </span>
                <span className="text-xs text-yellow-600 font-medium" data-id="element-3453" data-mp="1iqxs8tj82hr7">
                  Medium
                </span>
                <span className="text-xs text-gray-600 font-medium pr-7" data-id="element-3454" data-mp="1msj6ojben43i">
                  High
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-gray-200 mt-4 pt-3" data-id="element-3455" data-mp="2ff3vpd4er43w">
            {['OVERVIEW', 'NOTIFICATIONS', 'DETAILS', 'FINANCIALS', 'PEOPLE', 'RESEARCH AGENTS', 'APPLICATIONS', 'AUDIT LOG'].map(tab => <motion.button key={tab} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => {
            setActiveTab(tab);
            setDecisionOpen(false);
          }} className={`flex-1 text-center py-1.5 text-xs font-semibold transition-colors ${tab === activeTab ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3456" data-mp="2ted8s3xbhi3n">
                {tab}
              </motion.button>)}
          </div>
        </div>

        {/* Credit Decision Section */}
        <div className="bg-green-50 border border-green-200 rounded-lg shadow-sm overflow-hidden" data-id="element-3457" data-mp="3v1qz1gxb9dcr">
          {/* Header Row */}
          <button onClick={() => setDecisionOpen(!decisionOpen)} className="w-full p-4 flex items-center justify-between bg-green-100 hover:bg-green-150 transition-colors" data-id="element-3458" data-mp="8rn1z814ze0q">
            <div className="flex items-center gap-4" data-id="element-3459" data-mp="1k84nid373w8l">
              <span className="font-bold text-gray-900" data-id="element-3460" data-mp="1k9ft8ig7be2c">Credit Decision</span>
              <span className="border border-green-600 text-green-700 px-2.5 py-0.5 rounded-full text-sm font-medium" data-id="element-3461" data-mp="3drn46ljduj3b">
                Approved
              </span>
              <span className="text-sm text-gray-500" data-id="element-3462" data-mp="3nnyg3af296x8">
                Date: January 10, 2026
              </span>
              <span className="text-sm text-gray-300" data-id="element-3463" data-mp="3sfzy7cywtrv6">·</span>
              <span className="text-sm font-medium text-gray-700" data-id="element-3464" data-mp="18x9qtaghzon">
                Approved Credit Limit: $100,000
              </span>
            </div>
            {decisionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-3465" data-mp="5c3ajlesuynq" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-3466" data-mp="aa4z2luzvq6l" />}
          </button>

          {/* Expanded Content */}
          {decisionOpen && <div className="p-6 border-t border-green-200" data-id="element-3467" data-mp="2c1qcyrpysj8k">
              <div className="grid grid-cols-2 gap-x-12 gap-y-6 mb-8" data-id="element-3468" data-mp="2gzrw1cne4ik1">
                {/* Left Column */}
                <div className="space-y-4" data-id="element-3469" data-mp="29g0gnv2f6r9v">
                  <div data-id="element-3470" data-mp="29h246z0mh68h">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-3471" data-mp="29id6svic07y0">
                      Approved Decision
                    </p>
                    <p className="text-xl font-bold text-gray-900" data-id="element-3472" data-mp="1zq7hxundm6n1">
                      Approved for $100,000
                    </p>
                  </div>
                  <div data-id="element-3473" data-mp="1ut7apgesixuc">
                    <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-3474" data-mp="1upjgef1iu7ek">
                      Terms
                    </p>
                    <div className="text-sm text-gray-600 space-y-0.5" data-id="element-3475" data-mp="2nfic2ta812s7">
                      <p data-id="element-3476" data-mp="2ne738f1x54u8">Payment Terms Days: 30</p>
                      <p data-id="element-3477" data-mp="2ifnjmlass551">Discount: 2%</p>
                      <p data-id="element-3478" data-mp="2iiruu2mok157">Discount Days: 10</p>
                      <p data-id="element-3479" data-mp="2dgu46nbya428">Late Pay Penalty: 1.5%</p>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4" data-id="element-3480" data-mp="16zfgahnjkpsb">
                  <div data-id="element-3481" data-mp="121nkvdnouljd">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3482" data-mp="121du3nutkacm">
                      <p className="text-sm text-gray-500" data-id="element-3483" data-mp="1xm1w10mlahmv">Policy Applied</p>
                      <span className="text-sm text-gray-300" data-id="element-3484" data-mp="1sm720o7ipbxl">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-3485" data-mp="1sjugy26gqlgb">
                        (View Policy)
                      </button>
                    </div>
                    <p className="font-bold text-gray-900" data-id="element-3486" data-mp="22i0e66hbr3eu">
                      My Company's Credit Policy v1
                    </p>
                  </div>

                  <div data-id="element-3487" data-mp="2c8v1le6b491v">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3488" data-mp="2c9d4gtnh5lir">
                      <p className="text-sm text-gray-500" data-id="element-3489" data-mp="2q9xqrzsllzh0">
                        Credit Application
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-3490" data-mp="2lb4a5yi0lscm">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-3491" data-mp="vuv6qhmbj9lh">
                        (View Application)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-3492" data-mp="32jnbn7ld79ps">
                      Submitted on: January 10, 2026
                    </p>
                  </div>

                  <div data-id="element-3493" data-mp="37kum5eew6n05">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3494" data-mp="2sudng2tn6b6y">
                      <p className="text-sm text-gray-500" data-id="element-3495" data-mp="2ss12dgsl7kpo">
                        Recommendation Summary
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-3496" data-mp="2jsw8inrz0fju">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-3497" data-mp="2jvs6gc3tj48a">
                        (Full Reasoning)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-3498" data-mp="29xcgicgj210d">
                      Wonka Industries Ltd. is approved for credit based on
                      strong business fundamentals, an established 52-year
                      operating history in confectionery manufacturing, and
                      positive trade references. The company demonstrates
                      excellent financial health and consistent payment
                      patterns.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-4 gap-4" data-id="element-3499" data-mp="1pfwlrz0hnok">
                <button className="bg-gray-300 text-gray-500 py-3 rounded-lg font-semibold text-sm cursor-not-allowed" data-id="element-3500" data-mp="3tsgqq2enw0in">
                  APPROVE
                </button>
                <button className="bg-gray-300 text-gray-500 py-3 rounded-lg font-semibold text-sm cursor-not-allowed" data-id="element-3501" data-mp="pcp43ajtqe6b">
                  APPROVE WITH CHANGES
                </button>
                <button className="bg-gray-300 text-gray-500 py-3 rounded-lg font-semibold text-sm cursor-not-allowed" data-id="element-3502" data-mp="gkvivatcofae">
                  REJECT
                </button>
                <button className="border-2 border-gray-300 text-gray-400 py-3 rounded-lg font-semibold text-sm cursor-not-allowed" data-id="element-3503" data-mp="1d42xljtpofsv">
                  REGENERATE RECOMMENDATION
                </button>
              </div>
            </div>}
        </div>

        {/* Decision Modals */}
        <AnimatePresence data-id="element-3504" data-mp="2vy64997ghr31">
          {activeModal && <>
              {/* Backdrop */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-black/50 z-50" data-id="element-3505" data-mp="2zy6j2uygw4am" />

              {/* Modal */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3506" data-mp="3jpl6l1z8g8ea">
                <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-8" data-id="element-3507" data-mp="3f27wsnio27j8">
                  {activeModal === 'approve' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-3508" data-mp="3ts6r2f73oytf">
                        Approve Recommendation
                      </h2>
                      <div className="space-y-6" data-id="element-3509" data-mp="312q25riow01y">
                        <div className="grid grid-cols-3 gap-4" data-id="element-3510" data-mp="313hxd18djvrp">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3 col-span-2" data-id="element-3511" data-mp="2w452pub9hkek">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3512" data-mp="3b15x65ku7tgq">
                              Credit Limit
                            </legend>
                            <input type="text" defaultValue="100,000" className="w-full text-lg text-gray-900 outline-none" data-id="element-3513" data-mp="q8ubjv52hw6v" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3514" data-mp="xsv6059joa7y">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3515" data-mp="jtdevj8s0kvf">
                              Currency
                            </legend>
                            <select className="w-full text-lg text-gray-900 outline-none bg-transparent" data-id="element-3516" data-mp="nvh0h0p5t5uf">
                              <option data-id="element-3517" data-mp="1blbcxh5kgse5">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3518" data-mp="12sr982af3xmg">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3519" data-mp="16tkz317f7mhz">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-3520" data-mp="1ui47rhudu1al" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-4" data-id="element-3521" data-mp="254949zhdqgmx">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3522" data-mp="205fqe5y6knm8">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3523" data-mp="1v6bl3qedna16">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3524" data-mp="1q81ln3lmdn9r" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3525" data-mp="1cgmta65to9q1">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3526" data-mp="17ha9jua8ziro">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3527" data-mp="12jqrskeccnuz" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3528" data-mp="129u23ih3bgi5">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3529" data-mp="178x3msxv34ld">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3530" data-mp="endqu6xm1x4a" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3531" data-mp="3n25hhb69ohjk">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3532" data-mp="3n0b5imcz3zrm">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-3533" data-mp="1a3cdyrl4otp2" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-3534" data-mp="1f1dyzi247336">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-3535" data-mp="1f02vljckzxcl">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approved')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded" data-id="element-3536" data-mp="22qgqriqgqcbe">
                          APPROVE
                        </button>
                      </div>
                    </>}

                  {activeModal === 'approveWithChanges' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-3537" data-mp="2vdq7icazed96">
                        Approve and Input Values
                      </h2>
                      <div className="space-y-6" data-id="element-3538" data-mp="2hfrw9fryiwxw">
                        <div className="grid grid-cols-3 gap-4" data-id="element-3539" data-mp="2mbp9vebu5p6z">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3 col-span-2" data-id="element-3540" data-mp="27nvvfwyhkspz">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3541" data-mp="2cec99i67ygcv">
                              Credit Limit *
                            </legend>
                            <input type="text" defaultValue="100,000" className="w-full text-lg text-gray-900 outline-none" data-id="element-3542" data-mp="1yp0mid4254iz" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3543" data-mp="1tmrtjgr6q2gv">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3544" data-mp="1srwc1hlu3jld">
                              Currency
                            </legend>
                            <select className="w-full text-lg text-gray-900 outline-none bg-transparent" data-id="element-3545" data-mp="1iujanj8e86q7">
                              <option data-id="element-3546" data-mp="1ntcnd9fuvtpb">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3547" data-mp="2bjp3h7l98wqz">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3548" data-mp="2ghqmxtmley2z">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-3549" data-mp="26s74gp2sl68n" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-4" data-id="element-3550" data-mp="2zaujbmr73p1a">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3551" data-mp="nsr42lo4oq0b">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3552" data-mp="ito8rsxy9y5j">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3553" data-mp="skit2rrmynoc" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3554" data-mp="3w32at0coub4w">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-3555" data-mp="dwxr516hyui0">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3556" data-mp="1pa08hygf7o38" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3557" data-mp="mwkirfjjsfwa">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3558" data-mp="rvx6e2i8tqzx">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-3559" data-mp="vvxr24vxqyff" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3560" data-mp="87ej5qgssnnv">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3561" data-mp="d07p2ho0621j">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-3562" data-mp="3ckux1proihcd" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-3563" data-mp="346q9aet7o2g7">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-3564" data-mp="303tl88kwje4v">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approvedWithChanges')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded" data-id="element-3565" data-mp="2lffjy234vxqf">
                          CONFIRM APPROVAL
                        </button>
                      </div>
                    </>}

                  {activeModal === 'reject' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-3566" data-mp="22iebu8ea9hi9">
                        Reject
                      </h2>
                      <div className="space-y-6" data-id="element-3567" data-mp="bcrljwyy8c5o">
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-3568" data-mp="bbgi5y9f16f3">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-3569" data-mp="3ss82ove6qpeq">
                            Reasoning
                          </legend>
                          <textarea rows={6} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-3570" data-mp="3jsbaijsl78ex" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-3571" data-mp="39vq5vzkhbzbm">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-3572" data-mp="3eu1fqaary9tu">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('rejected')} className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded" data-id="element-3573" data-mp="2xiuv5ab9fj62">
                          CONFIRM REJECTION
                        </button>
                      </div>
                    </>}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {activeTab === 'OVERVIEW' ? <WonkaOverviewTab data-id="element-3574" data-mp="3ba9czdtflebj" /> : activeTab === 'FINANCIALS' ? <WonkaFinancialsTab data-id="element-3575" data-mp="3q8l99jcw6l26" /> : activeTab === 'PEOPLE' ? <WonkaPeopleTab data-id="element-3576" data-mp="3l98mszpxng04" /> : activeTab === 'DETAILS' ? <WonkaDetailsTab data-id="element-3577" data-mp="3la07hfhzrtbm" /> : activeTab === 'NOTIFICATIONS' ? <WonkaNotificationsTab data-id="element-3578" data-mp="1vvbupd5ho9d2" /> : activeTab === 'RESEARCH AGENTS' ? <WonkaResearchAgentsTab data-id="element-3579" data-mp="1qvz9sxy6hcd4" /> : activeTab === 'APPLICATIONS' ? <WonkaApplicationView data-id="element-3580" data-mp="2gjmjc73ur05s" /> : activeTab === 'AUDIT LOG' ? <WonkaAuditLogTab data-id="element-3581" data-mp="2glpknnaggfpt" /> : <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center text-gray-500" data-id="element-3582" data-mp="2kk6ithbwefv3">
            Content for {activeTab} tab coming soon.
          </div>}
      </div>

      <NotesModal isOpen={showNotesModal} onClose={() => setShowNotesModal(false)} notes={wonkaNotes} companyName="WONKA INDUSTRIES" data-id="element-3583" data-mp="2uje2snd518r6" />

      <ScoutAIButton companyName="wonka" data-id="element-3584" data-mp="33jkf45x3o2or" />
    </div>;
}